import { useState } from 'react';
import Link from 'next/link';
import MarketingLayout from '../components/MarketingLayout';
import { FREE_HISTORY_DAYS } from '../lib/constants';
import { auth } from '../auth';

const PAYWALL_REASONS = {
  canceled: 'Your Pro subscription was canceled. Re-subscribe below to get Pro back.',
  unpaid: 'Your last payment did not go through. Re-subscribe to restore Pro.',
  incomplete: 'We did not receive a completed payment. Try the checkout again.',
  trial_expired: 'Your Pro trial ended. Re-subscribe below to keep your full history.',
};

// Reasons that mean the user had a subscription before — show resubscribe
// wording instead of the free-trial pitch.
const RESUBSCRIBE_REASONS = new Set(['canceled', 'unpaid', 'incomplete', 'trial_expired']);

export async function getServerSideProps(context) {
  const reason = context.query.reason;
  const session = await auth(context.req, context.res);
  return {
    props: {
      reason: typeof reason === 'string' ? reason : null,
      signedIn: Boolean(session?.user?.id),
    },
  };
}

const FREE_BENEFITS = [
  'Full daily planner — goals, targets, time blocks, wins',
  'Daily anchor quotes',
  `Last ${FREE_HISTORY_DAYS} days of history`,
  'Any device',
  'No credit card required',
];

const PRO_BENEFITS = [
  'Everything in Free',
  'Unlimited history — every day you\u2019ve ever planned',
  'Connect AI agents (MCP + API access)',
  'Support an independent product',
];

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      className="h-6 w-6 text-green-500"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function BenefitList({ benefits }) {
  return (
    <ul className="space-y-4">
      {benefits.map((benefit) => (
        <li key={benefit} className="flex items-start">
          <div className="flex-shrink-0">
            <CheckIcon />
          </div>
          <p className="ml-3 text-base leading-6 font-medium text-gray-500">{benefit}</p>
        </li>
      ))}
    </ul>
  );
}

export default function Pricing({ reason, signedIn }) {
  const reasonMessage = reason ? PAYWALL_REASONS[reason] : null;
  const resubscribing = signedIn && reason && RESUBSCRIBE_REASONS.has(reason);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  async function handleCheckout() {
    setSubmitting(true);
    setCheckoutError('');

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });

      const body = await response.json();
      if (!response.ok || !body.url) {
        setCheckoutError(body.error || 'Could not start checkout. Please try again.');
        setSubmitting(false);
        return;
      }

      window.location.href = body.url;
    } catch (err) {
      console.error('checkout error', err);
      setCheckoutError('Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <MarketingLayout
      title="Pricing - 10X Planner App | Free"
      description={`10X Planner is free to use — no credit card required. Pro ($15/month) adds unlimited history and AI agent access. 14-day free trial.`}
      canonical="/pricing"
    >
      <div className="pb-12">
        {reasonMessage && (
          <div className="mx-auto mt-6 max-w-2xl rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 border border-amber-200">
            {reasonMessage}
          </div>
        )}
        <div className="pt-8 px-4 sm:px-6 lg:px-8 lg:pt-12">
          <div className="text-center">
            <h2 className="text-lg leading-6 font-semibold text-gray-400 uppercase tracking-wider">
              Pricing
            </h2>
            <p className="mt-1 text-3xl leading-9 font-extrabold text-gray-800 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
              Free to use. Pro when you want more.
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
              The daily planner is free — build the habit, no credit card required. Upgrade to
              Pro for your full history and AI agent access.
            </p>
          </div>
        </div>

        <div className="mt-16 lg:mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-6 items-start">
              {/* Free tier */}
              <div className="rounded-lg shadow-lg border border-gray-200 bg-white">
                <div className="px-6 pt-10 pb-8">
                  <h3 className="text-center text-3xl leading-9 font-semibold text-gray-900">
                    Free
                  </h3>
                  <p className="mt-2 text-center text-sm text-gray-500">
                    The full daily planning ritual, free forever.
                  </p>
                  <div className="mt-4 flex items-center justify-center">
                    <span className="px-3 flex items-start text-6xl leading-none tracking-tight text-gray-900">
                      <span className="mt-2 mr-2 text-4xl font-medium">$</span>
                      <span className="font-extrabold">0</span>
                    </span>
                    <span className="text-2xl leading-8 font-medium text-gray-500">/month</span>
                  </div>
                </div>
                <div className="border-t-2 border-gray-100 rounded-b-lg pt-8 pb-8 px-6 bg-gray-50 sm:px-10">
                  <BenefitList benefits={FREE_BENEFITS} />
                  <div className="mt-8">
                    <div className="rounded-lg shadow-md">
                      <Link
                        href={signedIn ? '/planner' : '/login'}
                        className="block w-full text-center rounded-lg border border-blue-800 bg-white px-6 py-4 text-xl leading-6 font-medium text-blue-800 hover:bg-blue-50 focus:outline-none transition ease-in-out duration-150"
                      >
                        {signedIn ? 'Open the planner' : 'Start planning free'}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pro tier */}
              <div className="relative rounded-lg shadow-xl">
                <div className="pointer-events-none absolute inset-0 rounded-lg border-2 border-blue-800" />
                <div className="absolute inset-x-0 top-0 transform translate-y-px">
                  <div className="flex justify-center transform -translate-y-1/2">
                    <span className="inline-flex rounded-full bg-blue-800 px-4 py-1 text-sm leading-5 font-semibold tracking-wider uppercase text-white">
                      {resubscribing ? 'Welcome back' : '14-day free trial'}
                    </span>
                  </div>
                </div>
                <div className="bg-white rounded-t-lg px-6 pt-12 pb-8">
                  <h3
                    className="text-center text-3xl leading-9 font-semibold text-gray-900"
                    id="tier-pro"
                  >
                    Pro
                  </h3>
                  <p className="mt-2 text-center text-sm text-gray-500">
                    {resubscribing
                      ? 'Pick up where you left off:'
                      : 'Free for 14 days, then:'}
                  </p>
                  <div className="mt-4 flex items-center justify-center">
                    <span className="px-3 flex items-start text-6xl leading-none tracking-tight text-gray-900">
                      <span className="mt-2 mr-2 text-4xl font-medium">$</span>
                      <span className="font-extrabold">15</span>
                    </span>
                    <span className="text-2xl leading-8 font-medium text-gray-500">/month</span>
                  </div>
                  <p className="mt-3 text-center text-sm text-gray-500">
                    {resubscribing
                      ? 'Cancel anytime. Your history is right where you left it.'
                      : 'Cancel anytime. We don\u2019t charge until your trial ends.'}
                  </p>
                </div>
                <div className="border-t-2 border-gray-100 rounded-b-lg pt-8 pb-8 px-6 bg-gray-50 sm:px-10">
                  <BenefitList benefits={PRO_BENEFITS} />
                  <div className="mt-8">
                    {checkoutError && (
                      <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
                        {checkoutError}
                      </div>
                    )}
                    <div className="rounded-lg shadow-md">
                      {signedIn ? (
                        <button
                          type="button"
                          onClick={handleCheckout}
                          disabled={submitting}
                          aria-describedby="tier-pro"
                          className="block w-full text-center rounded-lg border border-transparent bg-blue-800 px-6 py-4 text-xl leading-6 font-medium text-white hover:bg-blue-900 focus:outline-none focus:border-blue-700 transition ease-in-out duration-150 disabled:opacity-60"
                        >
                          {submitting
                            ? 'Redirecting to checkout...'
                            : resubscribing
                              ? 'Re-subscribe'
                              : 'Start your 14-day Pro trial'}
                        </button>
                      ) : (
                        <Link
                          href="/login?callbackUrl=%2Fwelcome-back"
                          aria-describedby="tier-pro"
                          className="block w-full text-center rounded-lg border border-transparent bg-blue-800 px-6 py-4 text-xl leading-6 font-medium text-white hover:bg-blue-900 focus:outline-none focus:border-blue-700 transition ease-in-out duration-150"
                        >
                          Start your 14-day Pro trial
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-12 text-center text-base text-gray-500">
          Free means free — plan every day, forever, at no cost. Pro exists for the power users
          who want their whole story.
        </p>
      </div>
    </MarketingLayout>
  );
}
